/**
 * Cache and storage utilities for the Short Story Platform
 */

/**
 * Clear all browser storage and cache data
 * This is a comprehensive cleanup function that removes:
 * - localStorage data
 * - sessionStorage data
 * - Cookies
 * - Browser cache (if supported)
 */
export const clearAllCache = async () => {
  try {
    console.log('Starting comprehensive cache clearing...');
    
    // Clear localStorage
    const localStorageKeys = Object.keys(localStorage);
    localStorage.clear();
    console.log(`Cleared localStorage (${localStorageKeys.length} items)`);
    
    // Clear sessionStorage
    const sessionStorageKeys = Object.keys(sessionStorage);
    sessionStorage.clear();
    console.log(`Cleared sessionStorage (${sessionStorageKeys.length} items)`);
    
    // Clear all cookies for the current domain
    const cookies = document.cookie.split(";");
    cookies.forEach(function(c) { 
      document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/"); 
    });
    console.log(`Cleared cookies (${cookies.length} items)`);
    
    // Clear browser cache if supported
    if ('caches' in window) {
      try {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(name => caches.delete(name))
        );
        console.log(`Cleared browser cache (${cacheNames.length} caches)`);
      } catch (cacheError) {
        console.warn('Browser cache clearing failed:', cacheError);
      }
    } else {
      console.log('Browser cache API not supported');
    }
    
    console.log('✅ Cache clearing completed successfully');
    return true;
    
  } catch (error) {
    console.error('❌ Cache clearing failed:', error);
    return false;
  }
};

/**
 * Clear only authentication-related data
 * More targeted cleanup for auth issues
 */
export const clearAuthCache = () => {
  try {
    console.log('Clearing authentication cache...');
    
    // Clear auth-related localStorage items
    const authKeys = Object.keys(localStorage).filter(key => 
      key.includes('auth') || 
      key.includes('token') || 
      key.includes('user') ||
      key.includes('cognito') ||
      key.includes('amplify')
    );
    
    authKeys.forEach(key => localStorage.removeItem(key));
    console.log(`Cleared auth localStorage items: ${authKeys.join(', ')}`);
    
    // Clear auth-related sessionStorage items
    const sessionAuthKeys = Object.keys(sessionStorage).filter(key => 
      key.includes('auth') || 
      key.includes('token') || 
      key.includes('user') ||
      key.includes('cognito') ||
      key.includes('amplify')
    );
    
    sessionAuthKeys.forEach(key => sessionStorage.removeItem(key));
    console.log(`Cleared auth sessionStorage items: ${sessionAuthKeys.join(', ')}`);
    
    // Clear auth-related cookies
    const authCookies = document.cookie.split(";").filter(c => {
      const name = c.split('=')[0].trim().toLowerCase();
      return name.includes('auth') || name.includes('token') || name.includes('session');
    });
    
    authCookies.forEach(function(c) { 
      document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/"); 
    });
    
    console.log('✅ Auth cache clearing completed');
    return true;
    
  } catch (error) {
    console.error('❌ Auth cache clearing failed:', error);
    return false;
  }
};

/**
 * Emergency reset function - clears everything and reloads
 * Use this for troubleshooting when the app is in a bad state
 */
export const emergencyReset = async () => {
  console.log('🚨 Emergency reset initiated...');
  
  const success = await clearAllCache();
  
  if (success) {
    console.log('🔄 Reloading page in 2 seconds...');
    setTimeout(() => {
      window.location.reload();
    }, 2000);
  } else {
    console.log('⚠️ Cache clearing had issues, but reloading anyway...');
    setTimeout(() => {
      window.location.reload();
    }, 2000);
  }
};

/**
 * Make cache utilities available globally for console debugging
 * Usage in console:
 * - window.clearAllCache()
 * - window.clearAuthCache()
 * - window.emergencyReset()
 */
if (typeof window !== 'undefined') {
  window.clearAllCache = clearAllCache;
  window.clearAuthCache = clearAuthCache;
  window.emergencyReset = emergencyReset;
  
  // Add helpful console message
  console.log('🛠️ Cache utilities available:');
  console.log('  - window.clearAllCache() - Clear all browser data');
  console.log('  - window.clearAuthCache() - Clear only auth data');
  console.log('  - window.emergencyReset() - Nuclear option (clear + reload)');
}
